import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, MapPin } from 'lucide-react';
import { useUserLocation } from '../context/LocationContext';
import { API } from '../config/api';

/**
 * LocalTopCreatorsWidget
 * Compact sidebar widget: "🔥 Hot in {your city}" — top 5 local creators.
 * Falls back to platform-wide trending if user has no location.
 */
export default function LocalTopCreatorsWidget({ limit = 5, className = '' }) {
  const { location: userLocation, openLocationPicker } = useUserLocation();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCreators = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(limit) });
        if (userLocation?.city) params.set('city', userLocation.city);
        if (userLocation?.lat != null) params.set('lat', String(userLocation.lat));
        if (userLocation?.lng != null) params.set('lng', String(userLocation.lng));
        const res = await fetch(`${API}/local/top-creators?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          setCreators(data.creators || []);
        }
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchCreators();
  }, [userLocation?.city, userLocation?.lat, userLocation?.lng, limit]);

  const cityLabel = userLocation?.city || userLocation?.neighborhood;

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl bg-card/60 border border-border ${className}`} data-testid="local-creators-widget-loading">
        <div className="flex items-center gap-2 mb-3">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold">Loading local heat...</span>
        </div>
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-2 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-muted/40" />
              <div className="flex-1 h-3 rounded bg-muted/40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!creators.length) return null;

  return (
    <div className={`p-4 rounded-2xl bg-gradient-to-br from-orange-500/10 via-card/60 to-card/60 border border-orange-500/20 ${className}`} data-testid="local-creators-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-bold">
            {cityLabel ? `Hot in ${cityLabel}` : 'Trending creators'}
          </span>
        </div>
        {!cityLabel && (
          <button
            onClick={openLocationPicker}
            className="flex items-center gap-1 text-[10px] text-orange-400 hover:text-orange-300 transition-colors"
            data-testid="set-location-from-widget"
          >
            <MapPin className="w-3 h-3" /> Set city
          </button>
        )}
      </div>
      <ul className="space-y-2">
        {creators.map((c, idx) => (
          <li key={c.user_id}>
            <Link
              to={`/u/${c.handle || c.user_id}`}
              className="group flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-muted/30 transition-colors"
              data-testid={`local-creator-${idx + 1}`}
            >
              <span className="w-5 text-center text-xs font-bold text-orange-400">{idx + 1}</span>
              {c.avatar ? (
                <img src={c.avatar} alt={c.name} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-purple-600" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate group-hover:text-orange-400 transition-colors">
                  {c.name || c.handle || 'Creator'}
                </p>
                <p className="text-[10px] text-muted-foreground/60 truncate">
                  {c.follower_count} followers · {c.recent_posts} recent
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
